import { useEffect, useRef } from 'react'
import { getSession, onAuthStateChange } from '@/lib/auth'
import { useAuthStore } from '@/stores/authStore'
import { useUserStore } from '@/stores/userStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { keysToPurgeOnUserChange } from './sessionKeys'
import { useWsStore } from '@/stores/wsStore'
import { queryClient } from '@/lib/queryClient'
import { apiFetch } from '@/lib/api'
import type { UserProfile, UserPreferences, UserSettings } from '@/types/user'

/** localStorage keys used by the app that must be cleared on session change. */
/** Clear all cached/stored state so no data leaks between sessions. */
function clearAllSessionState() {
  queryClient.clear()
  useUserStore.getState().reset()
  useWsStore.getState().resetReconnect()
  // Clear the active workspace/org selection too — otherwise a different
  // account would inherit the previous user's selected workspace.
  useWorkspaceStore.getState().reset()
  // Purge per-account localStorage by PATTERN, not by a hardcoded list. The old
  // list named three keys and matched NONE of the five actually in the browser,
  // so a brand-new account inherited the previous user's selected project
  // (2026-09-06). See sessionKeys.ts for the full account.
  try {
    for (const key of keysToPurgeOnUserChange(localStorage)) {
      localStorage.removeItem(key)
    }
  } catch {
    /* private mode / storage disabled — nothing to purge */
  }
}

let _loadingUserId: string | null = null
async function loadUserData(userId: string) {
  // Deduplicate concurrent calls for the same user
  if (_loadingUserId === userId) return
  _loadingUserId = userId

  const store = useUserStore.getState()
  const authStore = useAuthStore.getState()

  // Plan 27 — call our own /api/auth/me/* endpoints instead of going through
  // Supabase's PostgREST shim. Apifetch attaches the qyngent JWT automatically.
  const [profileRes, prefsRes, settingsRes] = await Promise.allSettled([
    apiFetch<{ profile: UserProfile }>('/api/auth/me/profile'),
    apiFetch<{ preferences: UserPreferences }>('/api/auth/me/preferences'),
    apiFetch<{ settings: UserSettings }>('/api/auth/me/settings'),
  ])

  if (profileRes.status === 'fulfilled' && profileRes.value.profile) {
    store.setProfile(profileRes.value.profile)
  }
  if (prefsRes.status === 'fulfilled' && prefsRes.value.preferences) {
    store.setPreferences(prefsRes.value.preferences)
  }
  if (settingsRes.status === 'fulfilled' && settingsRes.value.settings) {
    store.setSettings(settingsRes.value.settings)
  }
  store.setProfileLoaded(true)

  // Load admin status + workspace context from API
  authStore.setAdminLoading(true)
  try {
    const me = await apiFetch<{ is_platform_admin: boolean; is_org_admin: boolean; org_id: string | null; workspace_id: string | null }>('/api/auth/me')
    authStore.setPlatformAdmin(me.is_platform_admin ?? false)
    authStore.setOrgAdmin(me.is_org_admin ?? false)
    // Populate workspace store for the session
    const wsStore = useWorkspaceStore.getState()
    if (me.org_id) wsStore.setActiveOrg(me.org_id)
    // INITIALIZE the active workspace only when the user hasn't already
    // selected one (the store is persisted). Previously this ran on every
    // user-data load and clobbered a manual "Set active" / switcher choice
    // with the backend's DEFAULT workspace on the next navigation/re-auth —
    // which also made "New Project" silently land in Default (bug fixed
    // 2026-09-02). A user-switch clears the store (clearAllSessionState), so
    // the next account still initializes correctly.
    if (!wsStore.activeWorkspaceId) {
      if (me.workspace_id) {
        wsStore.setActiveWorkspace(me.workspace_id)
      } else if (me.org_id) {
      // Fallback: backend didn't resolve a default workspace. Fetch the org's
      // workspaces and auto-select the default (or first) so single-workspace
      // users don't get stuck behind a hidden switcher.
      try {
        const { workspaces } = await apiFetch<{
          workspaces: Array<{ id: string; is_default?: boolean }>
          // Trailing slash on `/workspaces/` matters: without it FastAPI
          // 307-redirects to the slashy path with an absolute Location
          // header pointing at the internal Docker hostname
          // (`dashboard-api:8100`), which the browser then can't resolve.
        }>(`/api/workspaces/?org_id=${me.org_id}`)
        const target = workspaces.find((w) => w.is_default) ?? workspaces[0]
        if (target) wsStore.setActiveWorkspace(target.id)
      } catch {
        /* best-effort; leave activeWorkspaceId null */
      }
      }
    }
  } catch {
    authStore.setPlatformAdmin(false)
    authStore.setOrgAdmin(false)
  } finally {
    _loadingUserId = null
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const setAuth = useAuthStore((s) => s.setAuth)
  const setLoading = useAuthStore((s) => s.setLoading)
  const resetUser = useUserStore((s) => s.reset)
  /** Track the current user ID so we can detect user switches (not just logout). */
  const prevUserIdRef = useRef<string | null>(null)

  useEffect(() => {
    setLoading(true)

    // Plan 27 — use our vendor-neutral auth client.
    getSession().then((session) => {
      const userId = session?.user?.id ?? null
      prevUserIdRef.current = userId
      setAuth(session?.user ?? null, session)
      if (session?.user) {
        loadUserData(session.user.id)
      } else {
        useUserStore.getState().setProfileLoaded(true)
      }
    })

    const unsubscribe = onAuthStateChange((_event, session) => {
      const newUserId = session?.user?.id ?? null
      const previousUserId = prevUserIdRef.current

      // User changed (logout, or switched to a different account) — purge everything
      if (previousUserId && previousUserId !== newUserId) {
        clearAllSessionState()
      }

      prevUserIdRef.current = newUserId
      setAuth(session?.user ?? null, session)

      if (session?.user) {
        loadUserData(session.user.id)
      } else {
        // Signed out — ensure stores are clean (clearAllSessionState already ran above,
        // but this covers the case where previousUserId was null)
        resetUser()
      }
    })

    return () => {
      unsubscribe()
    }
  }, [setAuth, setLoading, resetUser])

  return <>{children}</>
}
